"use client";
import React, { useState } from "react";
import { makeRequestAuth } from "../../_config/api";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";
import { AiFillEye, AiFillEyeInvisible } from "react-icons/ai";

export default function SecuritySettings() {
  const [changePass, setChangePass] = useState({
    loading: false,
    data: {
      oldPassword: "",
      newPassword: "",
      conformPassword: "",
    },
  });

  const [matchError, setMatchError] = useState(false);

  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const validatePassword = (password) => {
    return {
      length: password.length >= 8,
      upper: /[A-Z]/.test(password),
      lower: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[^A-Za-z0-9]/.test(password),
    };
  };

  const validations = validatePassword(changePass.data.newPassword);
  const allValid = Object.values(validations).every(Boolean);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setChangePass((prev) => ({
      ...prev,
      data: { ...prev.data, [name]: value },
    }));

    setMatchError(false);
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();

    const { oldPassword, newPassword, conformPassword } = changePass.data;

    if (newPassword !== conformPassword) {
      setMatchError(true);
      return;
    }
    if (!allValid) return;

    try {
      setChangePass((prev) => ({ ...prev, loading: true }));

      const res = await makeRequestAuth("POST", "/user/changePassword", {
        oldPassword,
        newPassword,
      });

      if (res?.error === false) {
        showSuccessToast("Password updated successfully!");

        setChangePass({
          loading: false,
          data: {
            oldPassword: "",
            newPassword: "",
            conformPassword: "",
          },
        });
      } else {
        showErrorToast(res?.message || "Something went wrong!");
      }
    } catch (error) {
      showErrorToast(error.message || "Server error bro, try again later 😅");
    } finally {
      setChangePass((prev) => ({ ...prev, loading: false }));
    }
  };

  const requirementText = (label, valid) => (
    <p className={`text-sm ${valid ? "text-green-500" : "text-red-500"}`}>
      {valid ? "✔" : "✘"} {label}
    </p>
  );

  return (
    <div className="p-4 md:p-6 border border-gray-300 dark:border-white/10 rounded-3xl mx-4 shadow-lg">
      <ToastContainer />
      <h2 className="text-2xl font-semibold text-gray-800 dark:text-white mb-8">
        Security Settings
      </h2>

      {/* ------------------ CHANGE PASSWORD ------------------ */}
      <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-4">
        Change Password
      </h3>

      <form
        onSubmit={handlePasswordChange}
        className="space-y-5 bg-gray-100 dark:bg-[#1E2433] p-4 rounded-2xl"
      >
        <div>
          <label className="block text-gray-700 dark:text-gray-300 mb-1">
            Current Password
          </label>

          <div className="relative">
            <input
              type={showOld ? "text" : "password"}
              name="oldPassword"
              value={changePass.data.oldPassword}
              onChange={handleInputChange}
              placeholder="Enter Current Password"
              className="w-full p-3 pr-12 rounded-lg bg-white dark:bg-[#2A3142] border border-gray-300 dark:border-white/10 text-gray-800 dark:text-white"
            />

            <span
              onClick={() => setShowOld(!showOld)}
              className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
            >
              {showOld ? (
                <AiFillEyeInvisible size={22} />
              ) : (
                <AiFillEye size={22} />
              )}
            </span>
          </div>
        </div>

        <div>
          <label className="block text-gray-700 dark:text-gray-300 mb-1">
            New Password
          </label>
          <div className="relative">
            <input
              type={showNew ? "text" : "password"}
              name="newPassword"
              value={changePass.data.newPassword}
              onChange={handleInputChange}
              placeholder="New Password"
              className={`w-full p-3 pr-12 rounded-lg bg-white dark:bg-[#2A3142] border ${
                !allValid && changePass.data.newPassword
                  ? "border-red-500"
                  : "border-gray-300 dark:border-white/10"
              } text-gray-800 dark:text-white`}
            />

            <span
              onClick={() => setShowNew(!showNew)}
              className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
            >
              {showNew ? (
                <AiFillEyeInvisible size={22} />
              ) : (
                <AiFillEye size={22} />
              )}
            </span>
          </div>
        </div>

        {/* Password Requirements UI */}
        {changePass.data.newPassword && (
          <div className="space-y-1 bg-white dark:bg-transparent p-2 rounded-md">
            {requirementText("At least 8 characters", validations.length)}
            {requirementText("One uppercase letter", validations.upper)}
            {requirementText("One lowercase letter", validations.lower)}
            {requirementText("One number", validations.number)}
            {requirementText("One special character", validations.special)}
          </div>
        )}

        <div>
          <label className="block text-gray-700 dark:text-gray-300 mb-1">
            Confirm Password
          </label>
          <div className="relative">
            <input
              type={showConfirm ? "text" : "password"}
              name="conformPassword"
              value={changePass.data.conformPassword}
              onChange={handleInputChange}
              placeholder="Confirm Password"
              className={`w-full p-3 pr-12 rounded-lg bg-white dark:bg-[#2A3142] border ${
                matchError
                  ? "border-red-500"
                  : "border-gray-300 dark:border-white/10"
              } text-gray-800 dark:text-white`}
            />

            <span
              onClick={() => setShowConfirm(!showConfirm)}
              className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-500"
            >
              {showConfirm ? (
                <AiFillEyeInvisible size={22} />
              ) : (
                <AiFillEye size={22} />
              )}
            </span>
          </div>
        </div>

        {matchError && (
          <p className="text-red-500 text-sm">
            New password aur Confirm password can't match!
          </p>
        )}

        <button
          type="submit"
          disabled={changePass.loading || !allValid}
          className="bg-pink-600 hover:bg-pink-700 disabled:bg-pink-400 text-white py-3 px-6 rounded-lg transition"
        >
          {changePass.loading ? "Updating..." : "Update Password"}
        </button>
      </form>
      {/* ------------------ 2FA ------------------ */}
      {/* <div className="mt-10">
        <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-4">
          Two-Factor Authentication (2FA)
        </h3>

        <div className="flex items-center justify-between bg-gray-100 dark:bg-[#1E2433] p-5 rounded-2xl">
          <div>
            <h4 className="text-gray-800 dark:text-white font-medium">
              Enable 2FA
            </h4>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Use OTP verification for extra security
            </p>
          </div>

          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={twoFA}
              onChange={() => setTwoFA(!twoFA)}
              className="sr-only peer"
            />

            <div className="w-11 h-6 bg-gray-300 rounded-full peer dark:bg-gray-600 peer-checked:bg-blue-600 transition-all duration-300"></div>

            <span className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow peer-checked:translate-x-5 transition-all duration-300"></span>
          </label>
        </div>
      </div> */}

      {/* ------------------ LOGIN ALERTS ------------------ */}
      {/* <div className="mt-10">
        <h3 className="text-xl font-semibold text-gray-700 dark:text-gray-200 mb-4">
          Login Alerts
        </h3>

        <div className="flex items-center justify-between bg-gray-100 dark:bg-[#1E2433] p-5 rounded-2xl">
          <div>
            <h4 className="text-gray-800 dark:text-white font-medium">
              Login Notifications
            </h4>
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              Get alert when suspicious login happens
            </p>
          </div>

          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              checked={loginAlerts}
              onChange={() => setLoginAlerts(!loginAlerts)}
              className="sr-only peer"
            />

            <div className="w-11 h-6 bg-gray-300 rounded-full peer dark:bg-gray-600 peer-checked:bg-blue-600 transition-all duration-300"></div>

            <span className="absolute left-0.5 top-0.5 w-5 h-5 bg-white rounded-full shadow peer-checked:translate-x-5 transition-all duration-300"></span>
          </label>
        </div>
      </div> */}
    </div>
  );
}
